import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { ArrowLeft } from 'lucide-react';
import type { Message } from './context/AskKikiContext';
import KikiBotIcon from '../../assets/Chat-bot_widget.png';

interface MessageBubbleProps {
  message: Message;
  showMainMenuButton?: boolean;
  onReturnToMainMenu?: () => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  showMainMenuButton = false,
  onReturnToMainMenu,
}) => {
  const isUser = message.sender === 'user';

  const sanitizedHtml = useMemo(() => {
    if (message.type === 'actions' || message.type === 'ticket_escalation') {
      return { __html: '' }; // These are handled specially in ChatWindow
    }
    const autolinkedText = (message.text || '').replace(/(^|\s)(https?:\/\/[^\s<]+)/g, '$1[$2]($2)');
    const rawMarkup = marked.parse(autolinkedText);
    
    DOMPurify.addHook('afterSanitizeAttributes', function(node) {
      if ('href' in node && node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    const clean = DOMPurify.sanitize(rawMarkup as string);
    DOMPurify.removeHook('afterSanitizeAttributes');
    
    return { __html: clean };
  }, [message.text, message.type]);

  if (message.type === 'actions' || message.type === 'ticket_escalation') {
    return null; // Handled in ChatWindow directly for these specific components
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex w-full mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-100 overflow-hidden mr-2 bg-white flex items-center justify-center">
          <img src={KikiBotIcon} alt="Kiki" className="w-6 h-6 object-contain" />
        </div>
      )}
      <div className="flex flex-col max-w-[80%] items-start">
        <div
          className={`relative w-full px-4 py-2 text-sm rounded-2xl ${
            isUser
              ? 'bg-blue-600 text-white rounded-br-none'
              : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none shadow-sm'
          }`}
        >
          <div
            className={`prose prose-sm max-w-none break-words [&_a]:break-all ${
              isUser 
                ? 'prose-invert text-white [&_a]:text-blue-200' 
                : 'text-gray-800 [&_a]:text-blue-600 [&_a]:underline hover:[&_a]:text-blue-800 [&_a]:transition-colors [&_a]:font-medium'
            } marker:text-current [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:w-full [&_th]:whitespace-nowrap [&_th]:min-w-[150px] [&_td]:min-w-[200px]`}
            dangerouslySetInnerHTML={sanitizedHtml}
          />
          <span className={`text-[10px] mt-1 block ${isUser ? 'text-blue-100 text-right' : 'text-gray-400'}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {!isUser && showMainMenuButton && onReturnToMainMenu && (
          <button
            onClick={onReturnToMainMenu}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-blue-200 bg-blue-50/80 hover:bg-blue-100 text-blue-700 text-xs font-semibold transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300 w-fit cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-blue-600" />
            Return to Main Menu
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default MessageBubble;
